import React, { useState, useCallback, useEffect } from "react";
import { KeyboardArrowDown, KeyboardArrowUp, Refresh, Search, Close } from "@mui/icons-material";
import LabelText from "../../../ui/LabelText";
import { SectionProps } from "../../../types/IPlayerSearchFilter";
import BarChart from "../../PlayerLanding/PlayerSearch/BarChart";
import { ISectionState } from "../../../types/IPlayerSearchFilter";
import { FilterSectionStyles } from "../../../styled-components/FilterStyles";
import styled from "styled-components";
import { useClubSearch } from "../../../contexts/ClubSearchContext";
import FilterDropdown from "../../../ui/FilterDropdown";
import { useLoading } from "../../../contexts/LoadingContext";
import SecondaryButton from "../../../ui/SecondaryButton";
import PrimaryButton from "../../../ui/PrimaryButton";
import { useNotification } from "../../../contexts/NotificationContext";
import { IClubFilterOptions } from "../../../types/IClubSearch";
import handleApiResponse from "../../../utils/handleApiResponse";
import IApiResponse from "../../../types/IApiResponse";
import apiUrls from "../../../config/apiUrls";
import { fetchData } from "../../../services/api";
import { IRegionAreaModel } from "../../../types/IRegionAreaData";
import { PlayerSearchFilterStyles } from "../../../styled-components/PlayerComponent.styles";

export interface ISelectedOption {
  region: string;
  optionId: number;
}

const StyledHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
  margin-bottom: 32px;
`;

const Section: React.FC<SectionProps> = ({ label, isOpen, toggle, children }) => (
  <div className="section">
    <div className="filters-sub-header" onClick={toggle}>
      <LabelText className="player-search-text">{label}</LabelText>
      {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
    </div>
    {isOpen && children}
  </div>
);

interface SelectedOption {
  region: string;
  optionId: number;
}

const ClubSearchFilter = () => {
  const {
    regionAreaData,
    isClubFilterChange,
    setIsClubFilterChange,
    clubfilterValues,
    setClubFilterValues,
    clubRegionValues,
    setClubRegionValues,
    setIsWageAnnual,
    setIsRevenueAnnual,
    WagesPA,
    setWagesPA,
    WagesPW,
    setWagesPW,
    clubrevenuePA,
    setclubRevenuePA,
    clubrevenuePW,
    setclubRevenuePW,
    selectedclubWagesLabel,
    setSelectedClubWagesLabel,
    selectedclubRevenueLabel,
    setSelectedclubRevenueLabel,
    currentPage,
    setCurrentPage,
  } = useClubSearch();
  const { showNotification, hideNotification, setDisplayNotification } = useNotification();
  const [region, setRegion] = useState<SelectedOption[]>([]);
  const [additionalData, setAdditionalData] = useState<string[]>([]);
  const [avgWagesPA, setAvgWagesPA] = useState<{ min: number; max: number }>({ min: 0, max: 180000000 });
  const [revenuePA, setRevenuePA] = useState<{ min: number; max: number }>({ min: 0, max: 500000000 });
  const [avgWagesPW, setAvgWagesPW] = useState<{ min: number; max: number }>({ min: 0, max: 180000000 });
  const [revenuePW, setRevenuePW] = useState<{ min: number; max: number }>({ min: 0, max: 180000000 });
  const [isOpen, setIsOpen] = useState<ISectionState>({
    tags: true,
    financial: true,
    statistical: true,
    status: true,
    region: true,
    clubleague: true,
    Preferredfoot: true,
  });
  const [selectedLabel, setSelectedLabel] = useState<string>("1");
  const [regionData, setRegionData] = useState<IRegionAreaModel[] | undefined>();
  const [selectedRevenueLabel, setSelectedRevenueLabel] = useState<string>("1");
  const [isSilderChange, setIsSliderChange] = useState<boolean>(false);

  const regions = regionAreaData === undefined || regionAreaData === null ? regionData : regionAreaData;

  useEffect(() => {
    if (additionalData.length > 0) showNotification("FilterNotification", generateMessage(), commonAction);
  }, [region, additionalData]);

  const addAdditionalData = (change: string) => {
    setAdditionalData((prevChanges) => {
      if (!prevChanges.includes(change)) {
        return [...prevChanges, change];
      }
      return prevChanges;
    });
  };

  const fetchAllAreaByRegions = async () => {
    const response: IApiResponse<any> = await fetchData(`${apiUrls["GetAllAreaByRegions"]}`);
    handleApiResponse(
      response,
      (regionareaData) => {
        setRegionData(regionareaData);
      },
      (error) => {
        console.error("Error fetching Home Team Data:", error);
      }
    );
  };

  useEffect(() => {
    if (regionAreaData === undefined) {
      fetchAllAreaByRegions();
    }
  }, []);

  useEffect(() => {
    setAvgWagesPW({ min: WagesPW.min, max: WagesPW.max });
    setAvgWagesPA({ min: WagesPA.min, max: WagesPA.max });
  }, [WagesPW, WagesPA]);

  useEffect(() => {
    setRevenuePW({ min: clubrevenuePW.min, max: clubrevenuePW.max });
    setRevenuePA({ min: clubrevenuePA.min, max: clubrevenuePA.max });
  }, [clubrevenuePW, clubrevenuePA]);

  useEffect(() => {
    setRegion(clubRegionValues);
  }, [clubRegionValues]);

  const handleSelectionRegionChange = (selectedOptions: ISelectedOption[], region: string) => {
    addAdditionalData("Regions");
    setRegion((prevRegion) => {
      const rolesByRegion: Record<string, ISelectedOption[]> = {};
      prevRegion.forEach((area) => {
        if (!rolesByRegion[area.region]) {
          rolesByRegion[area.region] = [];
        }
        rolesByRegion[area.region].push(area);
      });
      selectedOptions.forEach((newOption) => {
        const existingOptions = rolesByRegion[newOption.region];
        if (existingOptions) {
          existingOptions.forEach((existingOption) => {
            const index = prevRegion.findIndex((area) => area.optionId === existingOption.optionId);
            if (index !== -1) {
              prevRegion.splice(index, 1);
            }
          });
        }
      });
      let updatedRegions = [...prevRegion];
      if (selectedOptions.length === 0) {
        updatedRegions = updatedRegions.filter((area) => area.region !== region);
      } else {
        updatedRegions = [...updatedRegions, ...selectedOptions];
      }
      return updatedRegions;
    });
  };

  const handleCancel = () => {
    setAdditionalData([]);
    setIsClubFilterChange(false);
    hideNotification();
    const PrevSelectedRegion = region.filter((item) => clubfilterValues.areaId.includes(item.optionId));
    setRegion(PrevSelectedRegion);
  };

  const handleRunNewSearch = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const newClubFilterValues: IClubFilterOptions = {
      minRevenuePerWeek: revenuePW.min,
      maxRevenuePerWeek: revenuePW.max,
      minRevenueAnnual: revenuePA.min,
      maxRevenueAnnual: revenuePA.max,
      minAvWagePerWeek: avgWagesPW.min,
      maxAvWagePerWeek: avgWagesPW.max,
      minAvWageAnnual: avgWagesPA.min,
      maxAvWageAnnual: avgWagesPA.max,
      areaId: region.map((option) => option.optionId),
    };
    setClubFilterValues(newClubFilterValues);
    setDisplayNotification(false);
    hideNotification();
    setCurrentPage(1);
    setIsClubFilterChange(true);
  };

  const commonAction = (
    <div className="notification-action">
      <SecondaryButton label="Cancel" onClick={handleCancel} />
      <PrimaryButton iconType="refresh" label="Run new Search" onClick={handleRunNewSearch} />
    </div>
  );

  const generateMessage = () => {
    return (
      <div className="notification-message">
        <Search />
        <div className="message-content">
          <LabelText className="notification-title">Filters altered</LabelText>
          <div className="notification-message">
            <LabelText className="notification-text">Changes to: </LabelText>
            <LabelText className="notification-alter-text">{additionalData.join(", ")}</LabelText>
          </div>
        </div>
      </div>
    );
  };

  const toggleSection = useCallback((section: keyof ISectionState) => {
    setIsOpen((prevState) => ({ ...prevState, [section]: !prevState[section] }));
  }, []);

  const valueSliderData = [
    { value: 26, number: 5 },
    { value: 27, number: 20 },
    { value: 28, number: 10 },
    { value: 29, number: 25 },
    { value: 30, number: 30 },
    { value: 31, number: 35 },
    { value: 32, number: 50 },
    { value: 33, number: 70 },
    { value: 34, number: 40 },
    { value: 35, number: 60 },
    { value: 36, number: 30 },
    { value: 37, number: 25 },
  ];

  const wagesSliderData = [
    { wages: 26, number: 5 },
    { wages: 27, number: 20 },
    { wages: 28, number: 10 },
    { wages: 29, number: 25 },
    { wages: 30, number: 30 },
    { wages: 31, number: 35 },
    { wages: 32, number: 50 },
    { wages: 33, number: 70 },
    { wages: 34, number: 40 },
    { wages: 35, number: 60 },
    { wages: 36, number: 30 },
    { wages: 37, number: 25 },
  ];

  useEffect(() => {
    setSelectedLabel(selectedclubWagesLabel);
    setSelectedRevenueLabel(selectedclubRevenueLabel);
  }, [selectedclubWagesLabel, selectedclubRevenueLabel]);

  const handleOnWagesToggleChange = (SelectedTab: any) => {
    showNotification("FilterNotification", generateMessage(), commonAction);
    setSelectedLabel(SelectedTab.key);
    setIsWageAnnual(true);
  };

  const handleOnRevenueToggleChange = (SelectedTab: any) => {
    showNotification("FilterNotification", generateMessage(), commonAction);
    setSelectedRevenueLabel(SelectedTab.key);
    setIsRevenueAnnual(true);
  };

  const valuelabel = [
    { key: "1", value: "PER WEEK" },
    { key: "2", value: "ANNUAL" },
  ];

  const revenuelabel = [
    { key: "1", value: "PER WEEK" },
    { key: "2", value: "ANNUAL" },
  ];

  const handleWagesValueSliderChange = (min: number, max: number) => {
    showNotification("FilterNotification", generateMessage(), commonAction);
    if (selectedLabel === "1") {
      setAvgWagesPW((prevRange) => ({
        min: min !== undefined ? min : prevRange.min,
        max: max !== undefined ? max : prevRange.max,
      }));
      setWagesPW({ min, max });
    } else {
      setAvgWagesPA((prevRange) => ({
        min: min !== undefined ? min : prevRange.min,
        max: max !== undefined ? max : prevRange.max,
      }));
      setWagesPA({ min, max });
    }
    setIsSliderChange(true);
    addAdditionalData("wages");
  };

  const handleRevenueValueSliderChange = (min: number, max: number) => {
    showNotification("FilterNotification", generateMessage(), commonAction);
    if (selectedRevenueLabel === "1") {
      setRevenuePW((prevRange) => ({
        min: min !== undefined ? min : prevRange.min,
        max: max !== undefined ? max : prevRange.max,
      }));
    } else {
      setRevenuePA((prevRange) => ({
        min: min !== undefined ? min : prevRange.min,
        max: max !== undefined ? max : prevRange.max,
      }));
    }
    setIsSliderChange(true);
    addAdditionalData("revenue");
  };
  
  return (
    <FilterSectionStyles className="filters-section">
      <StyledHeader className="header-content">
        <LabelText className="player-family-name">Filters</LabelText>
      </StyledHeader>
      <div className="player-search-filter-wrapper">
        <PlayerSearchFilterStyles className="player-search-filter-sections">
          <Section label="Financial" isOpen={isOpen.financial} toggle={() => toggleSection("financial")}>
            <BarChart
              header="Av. WAGe"
              togglelabel={valuelabel}
              data={valueSliderData}
              onDataUpdate={handleWagesValueSliderChange}
              initialMinValue={0}
              initialMaxValue={180000000}
              defaultSelectedValue={selectedLabel === "1" ? [avgWagesPW.min, avgWagesPW.max] : [avgWagesPA.min, avgWagesPA.max]}
              isFor={"Transfer Value"}
              onChange={handleOnWagesToggleChange}
              selectedLabel={selectedLabel}
            />
            <BarChart
              header="Revenue"
              togglelabel={revenuelabel}
              data={wagesSliderData}
              onDataUpdate={handleRevenueValueSliderChange}
              initialMinValue={0}
              initialMaxValue={180000000}
              isFor={"Transfer Value"}
              defaultSelectedValue={selectedRevenueLabel === "1" ? [revenuePW.min, revenuePW.max] : [revenuePA.min, revenuePA.max]}
              onChange={handleOnRevenueToggleChange}
              selectedLabel={selectedRevenueLabel}
            />
          </Section>
          <Section label="Region" isOpen={isOpen.region} toggle={() => toggleSection("region")}>
            {regions?.map((regionItem) => (
              <FilterDropdown
                key={regionItem.region}
                region={regionItem.region}
                options={regionItem.areas}
                type={regionItem.region}
                onChange={handleSelectionRegionChange}
                defaultSelectedOptions={region.filter((item) => item.region === regionItem.region).map((item) => item.optionId)}
                includeAll={true}
                displayAsDropdown={true}
              />
            ))}
          </Section>
        </PlayerSearchFilterStyles>
      </div>
    </FilterSectionStyles>
  );
};

export default ClubSearchFilter;
